/**
 * Spot WebSocket API
 *
 * Access market data, manage accounts, and trade on Binance Spot.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import {
    WebsocketAPIBase,
    WebsocketSendMsgOptions,
    WebsocketApiResponse,
    WebsocketStream,
    createStreamHandler,
    randomString,
} from '@binance/common';
import { AccountApi } from './modules/account-api';
import { AuthApi } from './modules/auth-api';
import { GeneralApi } from './modules/general-api';
import { MarketApi } from './modules/market-api';
import { TradeApi } from './modules/trade-api';
import { UserDataStreamApi } from './modules/user-data-stream-api';

import type { UserDataStreamEventsResponse } from './types';
import type {
    AccountCommissionRequest,
    AccountRateLimitsOrdersRequest,
    AccountStatusRequest,
    AllOrderListsRequest,
    AllOrdersRequest,
    MyAllocationsRequest,
    MyFiltersRequest,
    MyPreventedMatchesRequest,
    MyTradesRequest,
    OpenOrderListsStatusRequest,
    OpenOrdersStatusRequest,
    OrderAmendmentsRequest,
    OrderListStatusRequest,
    OrderStatusRequest,
} from './modules/account-api';
import type {
    SessionLogonRequest,
    SessionLogoutRequest,
    SessionStatusRequest,
} from './modules/auth-api';
import type {
    ExchangeInfoRequest,
    ExecutionRulesRequest,
    PingRequest,
    TimeRequest,
} from './modules/general-api';
import type {
    AvgPriceRequest,
    BlockTradesHistoricalRequest,
    DepthRequest,
    KlinesRequest,
    ReferencePriceRequest,
    ReferencePriceCalculationRequest,
    TickerRequest,
    Ticker24hrRequest,
    TickerBookRequest,
    TickerPriceRequest,
    TickerTradingDayRequest,
    TradesAggregateRequest,
    TradesHistoricalRequest,
    TradesRecentRequest,
    UiKlinesRequest,
} from './modules/market-api';
import type {
    OpenOrdersCancelAllRequest,
    OrderAmendKeepPriorityRequest,
    OrderCancelRequest,
    OrderCancelReplaceRequest,
    OrderListCancelRequest,
    OrderListPlaceRequest,
    OrderListPlaceOcoRequest,
    OrderListPlaceOpoRequest,
    OrderListPlaceOpocoRequest,
    OrderListPlaceOtoRequest,
    OrderListPlaceOtocoRequest,
    OrderPlaceRequest,
    OrderTestRequest,
    SorOrderPlaceRequest,
    SorOrderTestRequest,
} from './modules/trade-api';
import type {
    SessionSubscriptionsRequest,
    UserDataStreamSubscribeRequest,
    UserDataStreamSubscribeSignatureRequest,
    UserDataStreamUnsubscribeRequest,
} from './modules/user-data-stream-api';

import type {
    AccountCommissionResponse,
    AccountRateLimitsOrdersResponse,
    AccountStatusResponse,
    AllOrderListsResponse,
    AllOrdersResponse,
    MyAllocationsResponse,
    MyFiltersResponse,
    MyPreventedMatchesResponse,
    MyTradesResponse,
    OpenOrderListsStatusResponse,
    OpenOrdersStatusResponse,
    OrderAmendmentsResponse,
    OrderListStatusResponse,
    OrderStatusResponse,
} from './types';
import type { SessionLogonResponse, SessionLogoutResponse, SessionStatusResponse } from './types';
import type {
    ExchangeInfoResponse,
    ExecutionRulesResponse,
    PingResponse,
    TimeResponse,
} from './types';
import type {
    AvgPriceResponse,
    BlockTradesHistoricalResponse,
    DepthResponse,
    KlinesResponse,
    ReferencePriceResponse,
    ReferencePriceCalculationResponse,
    TickerResponse,
    Ticker24hrResponse,
    TickerBookResponse,
    TickerPriceResponse,
    TickerTradingDayResponse,
    TradesAggregateResponse,
    TradesHistoricalResponse,
    TradesRecentResponse,
    UiKlinesResponse,
} from './types';
import type {
    OpenOrdersCancelAllResponse,
    OrderAmendKeepPriorityResponse,
    OrderCancelResponse,
    OrderCancelReplaceResponse,
    OrderListCancelResponse,
    OrderListPlaceResponse,
    OrderListPlaceOcoResponse,
    OrderListPlaceOpoResponse,
    OrderListPlaceOpocoResponse,
    OrderListPlaceOtoResponse,
    OrderListPlaceOtocoResponse,
    OrderPlaceResponse,
    OrderTestResponse,
    SorOrderPlaceResponse,
    SorOrderTestResponse,
} from './types';
import type {
    SessionSubscriptionsResponse,
    UserDataStreamSubscribeResponse,
    UserDataStreamSubscribeSignatureResponse,
    UserDataStreamUnsubscribeResponse,
} from './types';

export class WebsocketAPIConnection {
    private websocketBase: WebsocketAPIBase;
    private accountApi: AccountApi;
    private authApi: AuthApi;
    private generalApi: GeneralApi;
    private marketApi: MarketApi;
    private tradeApi: TradeApi;
    private userDataStreamApi: UserDataStreamApi;

    constructor(websocketBase: WebsocketAPIBase) {
        this.websocketBase = websocketBase;
        this.accountApi = new AccountApi(websocketBase);
        this.authApi = new AuthApi(websocketBase);
        this.generalApi = new GeneralApi(websocketBase);
        this.marketApi = new MarketApi(websocketBase);
        this.tradeApi = new TradeApi(websocketBase);
        this.userDataStreamApi = new UserDataStreamApi(websocketBase);
    }

    on(
        event: 'open' | 'message' | 'error' | 'close' | 'ping' | 'pong',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        listener: (...args: any[]) => void
    ) {
        this.websocketBase.on(event, listener);
    }

    off(
        event: 'open' | 'message' | 'error' | 'close' | 'ping' | 'pong',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        listener: (...args: any[]) => void
    ) {
        this.websocketBase.off(event, listener);
    }

    /**
     * Disconnects from the WebSocket server.
     * If there is no active connection, a warning is logged.
     * Otherwise, all connections in the connection pool are closed gracefully,
     * and a message is logged indicating that the connection has been disconnected.
     * @returns A Promise that resolves when all connections have been closed.
     * @throws Error if the WebSocket client is not set.
     */
    disconnect() {
        return this.websocketBase.disconnect();
    }

    /**
     * Checks if the WebSocket connection is currently open.
     * @returns `true` if the connection is open, `false` otherwise.
     */
    isConnected() {
        return this.websocketBase.isConnected();
    }

    /**
     * Sends a ping message to all connected Websocket servers in the pool.
     * If no connections are ready, a warning is logged.
     * For each active connection, the ping message is sent, and debug logs provide details.
     * @throws Error if a Websocket client is not set for a connection.
     */
    pingServer() {
        this.websocketBase.pingServer();
    }

    /**
     * Sends an unsigned message to the WebSocket server
     * @param method The API method to call
     * @param payload Message parameters and options
     * @returns Promise that resolves with the server response
     * @throws Error if not connected
     */
    sendMessage(method: string, payload: WebsocketSendMsgOptions = {}) {
        return this.websocketBase.sendMessage(method, payload);
    }

    /**
     * Sends a signed message to the WebSocket server with authentication
     * @param method The API method to call
     * @param payload Message parameters and options
     * @returns Promise that resolves with the server response
     * @throws Error if not connected
     */
    sendSignedMessage(method: string, payload: WebsocketSendMsgOptions = {}) {
        return this.websocketBase.sendMessage(method, payload, { isSigned: true });
    }

    /**
     * Get current account commission rates.
     *
     * Weight(IP): 20
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Database
     *
     * @summary Account Commission Rates (USER_DATA)
     * @param {AccountCommissionRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<AccountCommissionResponse>>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/account#account-commission Binance API Documentation}
     */
    accountCommission(
        requestParameters: AccountCommissionRequest
    ): Promise<WebsocketApiResponse<AccountCommissionResponse>> {
        return this.accountApi.accountCommission(requestParameters);
    }

    /**
     * Query your current unfilled order count for all intervals.
     *
     * Weight(IP): 40
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Memory
     *
     * @summary Unfilled Order Count (USER_DATA)
     * @param {AccountRateLimitsOrdersRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<AccountRateLimitsOrdersResponse>>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/account#account-rate-limits-orders Binance API Documentation}
     */
    accountRateLimitsOrders(
        requestParameters: AccountRateLimitsOrdersRequest = {}
    ): Promise<WebsocketApiResponse<AccountRateLimitsOrdersResponse>> {
        return this.accountApi.accountRateLimitsOrders(requestParameters);
    }

    /**
     * Query information about your account.
     *
     * Weight(IP): 20
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Memory => Database
     *
     * @summary Account information (USER_DATA)
     * @param {AccountStatusRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<AccountStatusResponse>>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/account#account-status Binance API Documentation}
     */
    accountStatus(
        requestParameters: AccountStatusRequest = {}
    ): Promise<WebsocketApiResponse<AccountStatusResponse>> {
        return this.accountApi.accountStatus(requestParameters);
    }

    /**
     * Query information about all your order lists, filtered by time range.
     *
     * Weight(IP): 20
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Database
     *
     * Notes:
     * If `startTime` and/or `endTime` are specified, `fromId` is ignored.
     * Order lists are filtered by `transactionTime` of the last order list execution status update.
     * If `fromId` is specified, return order lists with order list ID >= `fromId`.
     * If no condition is specified, the most recent order lists are returned.
     * The time between `startTime` and `endTime` can't be longer than 24 hours.
     *
     * @summary Account order list history (USER_DATA)
     * @param {AllOrderListsRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<AllOrderListsResponse>>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/account#all-order-lists Binance API Documentation}
     */
    allOrderLists(
        requestParameters: AllOrderListsRequest = {}
    ): Promise<WebsocketApiResponse<AllOrderListsResponse>> {
        return this.accountApi.allOrderLists(requestParameters);
    }

    /**
     * Query information about all your orders – active, canceled, filled – filtered by time range.
     *
     * Weight(IP): 20
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Database
     *
     * Notes:
     *
     * If `startTime` and/or `endTime` are specified, `orderId` is ignored.
     *
     * Orders are filtered by `time` of the last execution status update.
     *
     * If `orderId` is specified, return orders with order ID >= `orderId`.
     *
     * If no condition is specified, the most recent orders are returned.
     *
     * For some historical orders the `cummulativeQuoteQty` response field may be negative,
     * meaning the data is not available at this time.
     *
     * The time between `startTime` and `endTime` can't be longer than 24 hours.
     *
     * @summary Account order history (USER_DATA)
     * @param {AllOrdersRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<AllOrdersResponse>>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/account#all-orders Binance API Documentation}
     */
    allOrders(
        requestParameters: AllOrdersRequest
    ): Promise<WebsocketApiResponse<AllOrdersResponse>> {
        return this.accountApi.allOrders(requestParameters);
    }

    /**
     * Retrieves allocations resulting from SOR order placement.
     *
     * Weight(IP): 20
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Database
     *
     * Supported parameter combinations:
     *
     * Parameters                                  | Response |
     * ------------------------------------------- | -------- |
     * `symbol`                                    | allocations from oldest to newest |
     * `symbol` + `startTime`                      | oldest allocations since `startTime` |
     * `symbol` + `endTime`                        | newest allocations until `endTime` |
     * `symbol` + `startTime` + `endTime`          | allocations within the time range |
     * `symbol` + `fromAllocationId`               | allocations by allocation ID |
     * `symbol` + `orderId`                        | allocations related to an order starting with oldest |
     * `symbol` + `orderId` + `fromAllocationId`   | allocations related to an order by allocation ID |
     *
     **Note:** The time between `startTime` and `endTime` can't be longer than 24 hours.
     *
     * @summary Account allocations (USER_DATA)
     * @param {MyAllocationsRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<MyAllocationsResponse>>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/account#my-allocations Binance API Documentation}
     */
    myAllocations(
        requestParameters: MyAllocationsRequest
    ): Promise<WebsocketApiResponse<MyAllocationsResponse>> {
        return this.accountApi.myAllocations(requestParameters);
    }

    /**
     * Retrieves the list of [filters](/products/spot/filters) relevant to an account on a given symbol. This is the only method
     * that shows if an account has [`MAX_ASSET`](/products/spot/filters#max_asset) filters applied to it.
     *
     * Weight(IP): 40
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Memory
     *
     * @summary Query Relevant Filters (USER_DATA)
     * @param {MyFiltersRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<MyFiltersResponse>>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/account#my-filters Binance API Documentation}
     */
    myFilters(
        requestParameters: MyFiltersRequest
    ): Promise<WebsocketApiResponse<MyFiltersResponse>> {
        return this.accountApi.myFilters(requestParameters);
    }

    /**
     * Displays the list of orders that were expired due to STP.
     *
     * These are the combinations supported:
     *
     * `symbol` + `preventedMatchId`
     * `symbol` + `orderId`
     * `symbol` + `orderId` + `fromPreventedMatchId` (`limit` will default to 500)
     * `symbol` + `orderId` + `fromPreventedMatchId` + `limit`
     *
     * Weight: Case                            | Weight
     * ----                            | -----
     * If `symbol` is invalid          | 2
     * Querying by `preventedMatchId`  | 2
     * Querying by `orderId`           | 20
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Database
     *
     * @summary Account prevented matches (USER_DATA)
     * @param {MyPreventedMatchesRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<MyPreventedMatchesResponse>>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/account#my-prevented-matches Binance API Documentation}
     */
    myPreventedMatches(
        requestParameters: MyPreventedMatchesRequest
    ): Promise<WebsocketApiResponse<MyPreventedMatchesResponse>> {
        return this.accountApi.myPreventedMatches(requestParameters);
    }

    /**
     * Query information about all your trades, filtered by time range.
     *
     * Weight: Condition| Weight|
     * ---| ---
     * |Without orderId|20|
     * |With orderId|5|
     *
     * Security Type: USER_DATA
     *
     * Notes:
     * Data Source: Memory => Database
     *
     * Notes:
     * - If `fromId` is specified, return trades with trade ID >= `fromId`.
     * - If `startTime` and/or `endTime` are specified, trades are filtered by execution time (`time`).
     * - `fromId` cannot be used together with `startTime` and `endTime`.
     * - If `orderId` is specified, only trades related to that order are returned.
     * - `startTime` and `endTime` cannot be used together with `orderId`.
     * - If no condition is specified, the most recent trades are returned.
     * - The time between `startTime` and `endTime` can't be longer than 24 hours.
     *
     * @summary Account trade history (USER_DATA)
     * @param {MyTradesRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<MyTradesResponse>>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/account#my-trades Binance API Documentation}
     */
    myTrades(requestParameters: MyTradesRequest): Promise<WebsocketApiResponse<MyTradesResponse>> {
        return this.accountApi.myTrades(requestParameters);
    }

    /**
     * Query execution status of all open order lists.
     *
     * If you need to continuously monitor order status updates, please consider using WebSocket Streams:
     *
     * `userDataStream.subscribe` if on an authenticated session
     * `userDataStream.subscribe.signature` if subscribing through signature subscription
     *
     * Weight(IP): 6
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Memory -> Database
     *
     * @summary Current open Order lists (USER_DATA)
     * @param {OpenOrderListsStatusRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<OpenOrderListsStatusResponse>>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/account#open-order-lists-status Binance API Documentation}
     */
    openOrderListsStatus(
        requestParameters: OpenOrderListsStatusRequest = {}
    ): Promise<WebsocketApiResponse<OpenOrderListsStatusResponse>> {
        return this.accountApi.openOrderListsStatus(requestParameters);
    }

    /**
     * Query execution status of all open orders.
     *
     * If you need to continuously monitor order status updates, please consider using WebSocket Streams:
     *
     * `userDataStream.subscribe` if on an authenticated session
     * `userDataStream.subscribe.signature` if subscribing through signature subscription
     *
     * Weight: | Parameter | Weight |
     * | --------- | ------ |
     * | `symbol`  |      6 |
     * | none      |     80 |
     *
     * Security Type: USER_DATA
     *
     * Notes:
     * Data Source: Memory => Database
     *
     * @summary Current open orders (USER_DATA)
     * @param {OpenOrdersStatusRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<OpenOrdersStatusResponse>>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/account#open-orders-status Binance API Documentation}
     */
    openOrdersStatus(
        requestParameters: OpenOrdersStatusRequest = {}
    ): Promise<WebsocketApiResponse<OpenOrdersStatusResponse>> {
        return this.accountApi.openOrdersStatus(requestParameters);
    }

    /**
     * Queries all amendments of a single order.
     *
     * Weight(IP): 4
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Database
     *
     * @summary Query Order Amendments (USER_DATA)
     * @param {OrderAmendmentsRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<OrderAmendmentsResponse>>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/account#order-amendments Binance API Documentation}
     */
    orderAmendments(
        requestParameters: OrderAmendmentsRequest
    ): Promise<WebsocketApiResponse<OrderAmendmentsResponse>> {
        return this.accountApi.orderAmendments(requestParameters);
    }

    /**
     * Check execution status of an Order list.
     *
     * For execution status of individual orders, use `order.status`.
     *
     * Weight(IP): 4
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Database
     *
     * Notes:
     *
     * `origClientOrderId` refers to `listClientOrderId` of the order list itself.
     *
     * If both `origClientOrderId` and `orderListId` parameters are specified,
     * only `origClientOrderId` is used and `orderListId` is ignored.
     *
     * @summary Query Order list (USER_DATA)
     * @param {OrderListStatusRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<OrderListStatusResponse>>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/account#order-list-status Binance API Documentation}
     */
    orderListStatus(
        requestParameters: OrderListStatusRequest = {}
    ): Promise<WebsocketApiResponse<OrderListStatusResponse>> {
        return this.accountApi.orderListStatus(requestParameters);
    }

    /**
     * Check execution status of an order.
     *
     * Weight(IP): 4
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Memory => Database
     *
     * Notes:
     *
     * If both `orderId` and `origClientOrderId` are provided, the `orderId` is searched first, then the `origClientOrderId` from that result is checked against that order. If both conditions are not met the request will be rejected.
     *
     * For some historical orders the `cummulativeQuoteQty` response field may be negative,
     * meaning the data is not available at this time.
     *
     * @summary Query order (USER_DATA)
     * @param {OrderStatusRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<OrderStatusResponse>>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/account#order-status Binance API Documentation}
     */
    orderStatus(
        requestParameters: OrderStatusRequest
    ): Promise<WebsocketApiResponse<OrderStatusResponse>> {
        return this.accountApi.orderStatus(requestParameters);
    }

    /**
     * Authenticate WebSocket connection using the provided API key.
     *
     * After calling `session.logon`, you can omit `apiKey` and `signature` parameters for future requests that require them.
     *
     * Note that only one API key can be authenticated.
     *
     * Calling `session.logon` multiple times changes the current authenticated API key.
     *
     **Note:** Only Ed25519 keys are supported for this feature.
     *
     * Weight(IP): 2
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Memory
     *
     * @summary Log in with API key (USER_DATA)
     * @param {SessionLogonRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<SessionLogonResponse>[]>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/auth#session-logon Binance API Documentation}
     */
    sessionLogon(
        requestParameters: SessionLogonRequest = {}
    ): Promise<WebsocketApiResponse<SessionLogonResponse>[]> {
        return this.authApi.sessionLogon(requestParameters);
    }

    /**
     * Forget the API key previously authenticated. If the connection is not authenticated, this request does nothing.
     *
     * Note that the WebSocket connection stays open after `session.logout` request. You can continue using the connection, but now you will have to explicitly provide the `apiKey` and `signature` parameters where needed.
     *
     * Weight(IP): 2
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Memory
     *
     * @summary Log out of the session
     * @param {SessionLogoutRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<SessionLogoutResponse>[]>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/auth#session-logout Binance API Documentation}
     */
    sessionLogout(
        requestParameters: SessionLogoutRequest = {}
    ): Promise<WebsocketApiResponse<SessionLogoutResponse>[]> {
        return this.authApi.sessionLogout(requestParameters);
    }

    /**
     * Query the status of the WebSocket connection,
     * inspecting which API key (if any) is used to authorize requests.
     *
     * Weight(IP): 2
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Memory
     *
     * @summary Query session status
     * @param {SessionStatusRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<SessionStatusResponse>>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/auth#session-status Binance API Documentation}
     */
    sessionStatus(
        requestParameters: SessionStatusRequest = {}
    ): Promise<WebsocketApiResponse<SessionStatusResponse>> {
        return this.authApi.sessionStatus(requestParameters);
    }

    /**
     * Query current exchange trading rules, rate limits, and symbol
     * information.
     *
     * Weight(IP): 20
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Memory
     *
     **Notes:**
     * If the value provided to `symbol` or `symbols` do not exist, the endpoint will throw an error saying the symbol is invalid.
     * All parameters are optional.
     * Only one of `symbol`, `symbols`, `permissions` parameters can be specified.
     * Without parameters, `exchangeInfo` displays all symbols with `["SPOT", "MARGIN", "LEVERAGED"]` permissions.
     * In order to list *all* active symbols on the exchange, you need to explicitly request all permissions.
     * `permissions` accepts either a list of permissions, or a single permission name. E.g. `"SPOT"`.
     *
     **Examples of Symbol Permissions Interpretation from the Response:**
     *
     * `[["A","B"]]` means you may place an order if your account has either permission "A" **or** permission "B".
     * `[["A"],["B"]]` means you can place an order if your account has permission "A" **and** permission "B".
     * `[["A"],["B","C"]]` means you can place an order if your account has permission "A" **and** permission "B" or permission "C". (Inclusive or is applied here, not exclusive or, so your account may have both permission "B" and permission "C".)
     *
     * @summary Exchange information
     * @param {ExchangeInfoRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<ExchangeInfoResponse>>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/general#exchange-info Binance API Documentation}
     */
    exchangeInfo(
        requestParameters: ExchangeInfoRequest = {}
    ): Promise<WebsocketApiResponse<ExchangeInfoResponse>> {
        return this.generalApi.exchangeInfo(requestParameters);
    }

    /**
     * Query execution rules for symbols.
     *
     * Weight: Parameter | Weight
     * --- | ---
     * `symbol` | 2
     * `symbols` | 2 for each `symbol`, capped at a max of 40
     * `symbolStatus` | 40
     * None | 40
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Memory
     *
     **Note:** No combination of multiple parameters is allowed.
     *
     * @summary Query Execution Rules
     * @param {ExecutionRulesRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<ExecutionRulesResponse>>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/general#execution-rules Binance API Documentation}
     */
    executionRules(
        requestParameters: ExecutionRulesRequest = {}
    ): Promise<WebsocketApiResponse<ExecutionRulesResponse>> {
        return this.generalApi.executionRules(requestParameters);
    }

    /**
     * Test connectivity to the WebSocket API.
     *
     * Note: You can use regular WebSocket ping frames to test connectivity as well, WebSocket API will respond with pong frames as soon as possible. ping request along with time is a safe way to test request-response handling in your application.
     *
     * Weight(IP): 1
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Memory
     *
     * @summary Test connectivity
     * @param {PingRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<PingResponse>>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/general#ping Binance API Documentation}
     */
    ping(requestParameters: PingRequest = {}): Promise<WebsocketApiResponse<PingResponse>> {
        return this.generalApi.ping(requestParameters);
    }

    /**
     * Test connectivity to the WebSocket API and get the current server time.
     *
     * Weight(IP): 1
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Memory
     *
     * @summary Check server time
     * @param {TimeRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<TimeResponse>>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/general#time Binance API Documentation}
     */
    time(requestParameters: TimeRequest = {}): Promise<WebsocketApiResponse<TimeResponse>> {
        return this.generalApi.time(requestParameters);
    }

    /**
     * Get current average price for a symbol.
     *
     * Weight(IP): 2
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Memory
     *
     * @summary Current average price
     * @param {AvgPriceRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<AvgPriceResponse>>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/market#avg-price Binance API Documentation}
     */
    avgPrice(requestParameters: AvgPriceRequest): Promise<WebsocketApiResponse<AvgPriceResponse>> {
        return this.marketApi.avgPrice(requestParameters);
    }

    /**
     * Get block trades.
     *
     * Weight(IP): 25
     *
     * Security Type: NONE
     *
     * Notes:
     * - Data Source: Database
     *
     * @summary Historical Block Trades
     * @param {BlockTradesHistoricalRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<BlockTradesHistoricalResponse>>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/market#block-trades-historical Binance API Documentation}
     */
    blockTradesHistorical(
        requestParameters: BlockTradesHistoricalRequest
    ): Promise<WebsocketApiResponse<BlockTradesHistoricalResponse>> {
        return this.marketApi.blockTradesHistorical(requestParameters);
    }

    /**
     * Get current order book.
     *
     * Note that this request returns limited market depth.
     *
     * If you need to continuously monitor order book updates, please consider using WebSocket Streams:
     * `<symbol>@depth<levels>`
     * `<symbol>@depth`
     *
     * You can use `depth` request together with `<symbol>@depth` streams to [maintain a local order book](/products/spot/web-socket-streams#how-to-manage-a-local-order-book-correctly).
     *
     * Weight: Adjusted based on the limit:
     *
     * |Limit|Request Weight
     * ------|-------
     * 1-100|  5
     * 101-500| 25
     * 501-1000| 50
     * 1001-5000| 250
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Memory
     *
     * @summary Order book
     * @param {DepthRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<DepthResponse>>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/market#depth Binance API Documentation}
     */
    depth(requestParameters: DepthRequest): Promise<WebsocketApiResponse<DepthResponse>> {
        return this.marketApi.depth(requestParameters);
    }

    /**
     * Get klines (candlestick bars).
     *
     * Klines are uniquely identified by their open & close time.
     *
     * If you need access to real-time kline updates, please consider using WebSocket Streams:
     * `<symbol>@kline_<interval>`
     *
     * If you need historical kline data, please consider using [data.binance.vision](https://github.com/binance/binance-public-data/#klines).
     *
     * Weight(IP): 2
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Database
     *
     * Supported kline intervals (case-sensitive):
     *
     * Interval  | `interval` value
     * --------- | ----------------
     * seconds   | `1s`
     * minutes   | `1m`, `3m`, `5m`, `15m`, `30m`
     * hours     | `1h`, `2h`, `4h`, `6h`, `8h`, `12h`
     * days      | `1d`, `3d`
     * weeks     | `1w`
     * months    | `1M`
     *
     **Notes:**
     *
     * If `startTime` and `endTime` are not sent, the most recent klines are returned.
     * Supported values for `timeZone`:
     * Hours and minutes (e.g. `-1:00`, `05:45`)
     * Only hours (e.g. `0`, `8`, `4`)
     * Accepted range is strictly [-12:00 to +14:00] inclusive
     * If `timeZone` provided, kline intervals are interpreted in that timezone instead of UTC.
     * Note that `startTime` and `endTime` are always interpreted in UTC, regardless of `timeZone`.
     *
     * @summary Klines
     * @param {KlinesRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<KlinesResponse>>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/market#klines Binance API Documentation}
     */
    klines(requestParameters: KlinesRequest): Promise<WebsocketApiResponse<KlinesResponse>> {
        return this.marketApi.klines(requestParameters);
    }

    /**
     * Query Reference Price
     *
     * Weight(IP): 2
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Memory
     *
     * @summary Query Reference Price
     * @param {ReferencePriceRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<ReferencePriceResponse>>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/market#reference-price Binance API Documentation}
     */
    referencePrice(
        requestParameters: ReferencePriceRequest
    ): Promise<WebsocketApiResponse<ReferencePriceResponse>> {
        return this.marketApi.referencePrice(requestParameters);
    }

    /**
     * Query Reference Price Calculation
     *
     * Weight(IP): 2
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Memory
     *
     * @summary Query Reference Price Calculation
     * @param {ReferencePriceCalculationRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<ReferencePriceCalculationResponse>>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/market#reference-price-calculation Binance API Documentation}
     */
    referencePriceCalculation(
        requestParameters: ReferencePriceCalculationRequest
    ): Promise<WebsocketApiResponse<ReferencePriceCalculationResponse>> {
        return this.marketApi.referencePriceCalculation(requestParameters);
    }

    /**
     * Get rolling window price change statistics with a custom window.
     *
     * This request is similar to `ticker.24hr` but statistics are computed on demand using the arbitrary window you specify.
     *
     **Note:** Window size precision is limited to 1 minute.
     * While the `closeTime` is the current time of the request, `openTime` always start on a minute boundary.
     * As such, the effective window might be up to 59999 ms wider than the requested `windowSize`.
     *
     * <details>
     * <summary>Window computation example</summary>
     *
     * For example, a request for `"windowSize": "7d"` might result in the following window:
     *
     * ```javascript
     * {
     * "openTime": 1659580020000,
     * "closeTime": 1660184865291
     * }
     * ```
     *
     * Time of the request – `closeTime` – is 1660184865291 (August 11, 2022 02:27:45.291).
     * Requested window size should put the `openTime` 7 days before that – August 4, 02:27:45.291 –
     * but due to limited precision it ends up a bit earlier: 1659580020000 (August 4, 2022 02:27:00),
     * exactly at the start of a minute.
     * </details>
     *
     * If you need to continuously monitor trading statistics, please consider using WebSocket Streams:
     * `<symbol>@ticker_<window_size>` or `!ticker_<window-size>@arr`
     *
     * Weight: Adjusted based on the number of requested symbols:
     *
     * | Symbols | Weight |
     * |:-------:|:------:|
     * |    1–50 | 4 per symbol |
     * |  51–100 |    200 |
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Database
     *
     * Supported window sizes:
     *
     * Unit    | `windowSize` value
     * ------- | ------------------
     * minutes | `1m`, `2m` ... `59m`
     * hours   | `1h`, `2h` ... `23h`
     * days    | `1d`, `2d` ... `7d`
     *
     * Notes:
     *
     * Either `symbol` or `symbols` must be specified.
     *
     * Maximum number of symbols in one request: 200.
     *
     * Window size units cannot be combined.
     * E.g., <code>1d 2h</code> is not supported.
     *
     * @summary Rolling window price change statistics
     * @param {TickerRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<TickerResponse>>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/market#ticker Binance API Documentation}
     */
    ticker(requestParameters: TickerRequest = {}): Promise<WebsocketApiResponse<TickerResponse>> {
        return this.marketApi.ticker(requestParameters);
    }

    /**
     * Get 24-hour rolling window price change statistics.
     *
     * If you need to continuously monitor trading statistics, please consider using WebSocket Streams:
     *
     * `<symbol>@ticker` or `!ticker@arr`
     *
     * `<symbol>@miniTicker` or `!miniTicker@arr`
     *
     * If you need different window sizes,
     *
     * use the `ticker` request.
     *
     * Weight: Adjusted based on the number of requested symbols:
     *
     * |Parameter|Symbols Provided|Weight|
     * |---|---|---|
     * |symbol| 1 |2|
     * | |omitted| 80|
     * |symbols| 1-20 |2|
     * | | 21-100 |40|
     * | | 101+ |80|
     * | |omitted| 80|
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Memory
     *
     * Notes:
     *
     * `symbol` and `symbols` cannot be used together.
     *
     * If no symbol is specified, returns information about all symbols currently trading on the exchange.
     *
     * @summary 24hr ticker price change statistics
     * @param {Ticker24hrRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<Ticker24hrResponse>>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/market#ticker24hr Binance API Documentation}
     */
    ticker24hr(
        requestParameters: Ticker24hrRequest = {}
    ): Promise<WebsocketApiResponse<Ticker24hrResponse>> {
        return this.marketApi.ticker24hr(requestParameters);
    }

    /**
     * Get the current best price and quantity on the order book.
     *
     * If you need access to real-time order book ticker updates, please
     * consider using WebSocket Streams:
     *
     * `<symbol>@bookTicker`
     *
     * Weight: Adjusted based on the number of requested symbols:
     *
     * |Parameter|Symbols Provided|Weight|
     * |---|---|---|
     * |symbol| 1 |2|
     * | |omitted| 4|
     * |symbols| Any |4|
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Memory
     *
     * Notes:
     *
     * `symbol` and `symbols` cannot be used together.
     *
     * If no symbol is specified, returns information about all symbols currently trading on the exchange.
     *
     * @summary Symbol order book ticker
     * @param {TickerBookRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<TickerBookResponse>>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/market#ticker-book Binance API Documentation}
     */
    tickerBook(
        requestParameters: TickerBookRequest = {}
    ): Promise<WebsocketApiResponse<TickerBookResponse>> {
        return this.marketApi.tickerBook(requestParameters);
    }

    /**
     * Get the latest market price for a symbol.
     *
     * If you need access to real-time price updates, please consider using
     * WebSocket Streams:
     *
     * `<symbol>@aggTrade`
     *
     * `<symbol>@trade`
     *
     * Weight: Adjusted based on the number of requested symbols:
     *
     * |Parameter|Symbols Provided|Weight|
     * |---|---|---|
     * |symbol| 1 |2|
     * | |omitted| 4|
     * |symbols| Any |4|
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Memory
     *
     * Notes:
     *
     * `symbol` and `symbols` cannot be used together.
     *
     * If no symbol is specified, returns information about all symbols currently trading on the exchange.
     *
     * @summary Symbol price ticker
     * @param {TickerPriceRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<TickerPriceResponse>>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/market#ticker-price Binance API Documentation}
     */
    tickerPrice(
        requestParameters: TickerPriceRequest = {}
    ): Promise<WebsocketApiResponse<TickerPriceResponse>> {
        return this.marketApi.tickerPrice(requestParameters);
    }

    /**
     * Price change statistics for a trading day.
     *
     * Weight: 4 for each requested symbol regardless of windowSize. The weight for this request will cap at 200 once the number of symbols in the request is more than 50.
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Database
     *
     **Notes:**
     *
     * Supported values for `timeZone`:
     * Hours and minutes (e.g. `-1:00`, `05:45`)
     * Only hours (e.g. `0`, `8`, `4`)
     *
     *
     * @summary Trading Day Ticker
     * @param {TickerTradingDayRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<TickerTradingDayResponse>>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/market#ticker-trading-day Binance API Documentation}
     */
    tickerTradingDay(
        requestParameters: TickerTradingDayRequest = {}
    ): Promise<WebsocketApiResponse<TickerTradingDayResponse>> {
        return this.marketApi.tickerTradingDay(requestParameters);
    }

    /**
     * Get aggregate trades.
     *
     * An *aggregate trade* (aggtrade) represents one or more individual
     * trades.
     *
     * Trades that fill at the same time, from the same taker order, with the
     * same price –
     *
     * those trades are collected into an aggregate trade with total quantity
     * of the individual trades.
     *
     * If you need access to real-time trading activity, please consider using
     * WebSocket Streams:
     *
     * `<symbol>@aggTrade`
     *
     * If you need historical aggregate trade data, please consider using [data.binance.vision](https://github.com/binance/binance-public-data/#aggtrades).
     *
     * Weight(IP): 4
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Database
     *
     * - If `fromId` is specified, return aggtrades with aggregate trade ID >= `fromId`. Use `fromId` and `limit` to page through all aggtrades.
     * - If `startTime` and/or `endTime` are specified, aggtrades are filtered by execution time (`T`). `fromId` cannot be used together with `startTime` and `endTime`.
     * - If no condition is specified, the most recent aggregate trades are returned.
     *
     * @summary Aggregate trades
     * @param {TradesAggregateRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<TradesAggregateResponse>>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/market#trades-aggregate Binance API Documentation}
     */
    tradesAggregate(
        requestParameters: TradesAggregateRequest
    ): Promise<WebsocketApiResponse<TradesAggregateResponse>> {
        return this.marketApi.tradesAggregate(requestParameters);
    }

    /**
     * Get historical trades.
     *
     * Weight(IP): 25
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Database
     *
     * Notes:
     *
     * If `fromId` is not specified, the most recent trades are returned.
     *
     * @summary Historical trades
     * @param {TradesHistoricalRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<TradesHistoricalResponse>>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/market#trades-historical Binance API Documentation}
     */
    tradesHistorical(
        requestParameters: TradesHistoricalRequest
    ): Promise<WebsocketApiResponse<TradesHistoricalResponse>> {
        return this.marketApi.tradesHistorical(requestParameters);
    }

    /**
     * Get recent trades.
     *
     * If you need access to real-time trading activity, please consider using
     * WebSocket Streams:
     *
     * `<symbol>@trade`
     *
     * Weight(IP): 25
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Memory
     *
     * @summary Recent trades
     * @param {TradesRecentRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<TradesRecentResponse>>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/market#trades-recent Binance API Documentation}
     */
    tradesRecent(
        requestParameters: TradesRecentRequest
    ): Promise<WebsocketApiResponse<TradesRecentResponse>> {
        return this.marketApi.tradesRecent(requestParameters);
    }

    /**
     * Get klines (candlestick bars) optimized for presentation.
     *
     * This request is similar to `klines`, having the same parameters and response. `uiKlines` return modified kline data, optimized for presentation of candlestick charts.
     *
     * Weight(IP): 2
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Database
     *
     * - If `startTime` and `endTime` are not sent, the most recent klines are returned.
     * - Supported values for `timeZone`:
     * - Hours and minutes (e.g. `-1:00`, `05:45`)
     * - Only hours (e.g. `0`, `8`, `4`)
     * - Accepted range is strictly [-12:00 to +14:00] inclusive
     * - If `timeZone` provided, kline intervals are interpreted in that timezone instead of UTC.
     * - Note that `startTime` and `endTime` are always interpreted in UTC, regardless of `timeZone`.
     *
     * @summary UI Klines
     * @param {UiKlinesRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<UiKlinesResponse>>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/market#ui-klines Binance API Documentation}
     */
    uiKlines(requestParameters: UiKlinesRequest): Promise<WebsocketApiResponse<UiKlinesResponse>> {
        return this.marketApi.uiKlines(requestParameters);
    }

    /**
     * Cancel all open orders on a symbol.
     * This includes orders that are part of an order list.
     *
     * Weight(IP): 1
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     * @summary Cancel open orders (TRADE)
     * @param {OpenOrdersCancelAllRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<OpenOrdersCancelAllResponse>>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/trade#open-orders-cancel-all Binance API Documentation}
     */
    openOrdersCancelAll(
        requestParameters: OpenOrdersCancelAllRequest
    ): Promise<WebsocketApiResponse<OpenOrdersCancelAllResponse>> {
        return this.tradeApi.openOrdersCancelAll(requestParameters);
    }

    /**
     * Reduce the quantity of an existing open order.
     *
     * This adds 0 orders to the `EXCHANGE_MAX_ORDERS` filter and the `MAX_NUM_ORDERS` filter.
     *
     * Read [Order Amend Keep Priority FAQ](/products/spot/faqs/order_amend_keep_priority) to learn more.
     *
     * Weight(IP): 4
     *
     * Unfilled Order Count: 0
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     * @summary Order Amend Keep Priority (TRADE)
     * @param {OrderAmendKeepPriorityRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<OrderAmendKeepPriorityResponse>>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/trade#order-amend-keep-priority Binance API Documentation}
     */
    orderAmendKeepPriority(
        requestParameters: OrderAmendKeepPriorityRequest
    ): Promise<WebsocketApiResponse<OrderAmendKeepPriorityResponse>> {
        return this.tradeApi.orderAmendKeepPriority(requestParameters);
    }

    /**
     * Cancel an active order.
     *
     * Weight(IP): 1
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     * Notes:
     *
     * If both `orderId` and `origClientOrderId` parameters are provided, the `orderId` is searched first, then the `origClientOrderId` from that result is checked against that order. If both conditions are not met the request will be rejected.
     *
     * `newClientOrderId` will replace `clientOrderId` of the canceled order, freeing it up for new orders.
     *
     * If you cancel an order that is a part of an order list, the entire order list is canceled.
     *
     * The performance for canceling an order (single cancel or as part of a cancel-replace) is always better when only `orderId` is sent. Sending `origClientOrderId` or both `orderId` + `origClientOrderId` will be slower.
     *
     * @summary Cancel order (TRADE)
     * @param {OrderCancelRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<OrderCancelResponse>>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/trade#order-cancel Binance API Documentation}
     */
    orderCancel(
        requestParameters: OrderCancelRequest
    ): Promise<WebsocketApiResponse<OrderCancelResponse>> {
        return this.tradeApi.orderCancel(requestParameters);
    }

    /**
     * * Cancel an existing order and immediately place a new order instead of the canceled one.
     * A new order that was not attempted (i.e. when `newOrderResult: NOT_ATTEMPTED`), will still increase the unfilled order count by 1.
     * You can only cancel an individual order from an orderList using this method, but the result is the same as canceling the entire orderList.not attempted (i.e. when `newOrderResult: NOT_ATTEMPTED`), will still increase the unfilled order count by 1.
     *
     * Weight(IP): 1
     *
     * Unfilled Order Count: 1
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     * Similar to the [`order.place`](#order-place) request,
     * additional mandatory parameters (*) are determined by the new order `type`.
     *
     * Available `cancelReplaceMode` options:
     *
     * `STOP_ON_FAILURE` – if cancellation request fails, new order placement will not be attempted.
     * `ALLOW_FAILURE` – new order placement will be attempted even if the cancel request fails.
     *
     * <table>
     * <thead>
     * <tr>
     * <th colspan=3 align=left>Request</th>
     * <th colspan=3 align=left>Response</th>
     * </tr>
     * <tr>
     * <th><code>cancelReplaceMode</code></th>
     * <th><code>orderRateLimitExceededMode</code></th>
     * <th>Unfilled Order Count</th>
     * <th><code>cancelResult</code></th>
     * <th><code>newOrderResult</code></th>
     * <th><code>status</code></th>
     * </tr>
     * </thead>
     * <tbody>
     * <tr>
     * <td rowspan="11"><code>STOP_ON_FAILURE</code></td>
     * <td rowspan="6"><code>DO_NOTHING</code></td>
     * <td rowspan="3">Within Limits</td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td align=right><code>200</code></td>
     * </tr>
     * <tr>
     * <td>❌ <code>FAILURE</code></td>
     * <td>➖ <code>NOT_ATTEMPTED</code></td>
     * <td align=right><code>400</code></td>
     * </tr>
     * <tr>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>❌ <code>FAILURE</code></td>
     * <td align=right><code>409</code></td>
     * </tr>
     * <tr>
     * <td rowspan="3">Exceeds Limits</td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td align=right>N/A</td>
     * </tr>
     * <tr>
     * <td>❌ <code>FAILURE</code></td>
     * <td>➖ <code>NOT_ATTEMPTED</code></td>
     * <td align=right>N/A</td>
     * </tr>
     * <tr>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>❌ <code>FAILURE</code></td>
     * <td align=right>N/A</td>
     * </tr>
     * <tr>
     * <td rowspan="5"><code>CANCEL_ONLY</code></td>
     * <td rowspan="3">Within Limits</td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td align=right><code>200</code></td>
     * </tr>
     * <tr>
     * <td>❌ <code>FAILURE</code></td>
     * <td>➖ <code>NOT_ATTEMPTED</code></td>
     * <td align=right><code>400</code></td>
     * </tr>
     * <tr>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>❌ <code>FAILURE</code></td>
     * <td align=right><code>409</code></td>
     * </tr>
     * <tr>
     * <td rowspan="2">Exceeds Limits</td>
     * <td>❌ <code>FAILURE</code></td>
     * <td>➖ <code>NOT_ATTEMPTED</code></td>
     * <td align=right><code>429</code></td>
     * </tr>
     * <tr>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>❌ <code>FAILURE</code></td>
     * <td align=right><code>429</code></td>
     * </tr>
     * <tr>
     * <td rowspan="16"><code>ALLOW_FAILURE</code></td>
     * <td rowspan="8"><code>DO_NOTHING</code></td>
     * <td rowspan="4">Within Limits</td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td align=right><code>200</code></td>
     * </tr>
     * <tr>
     * <td>❌ <code>FAILURE</code></td>
     * <td>❌ <code>FAILURE</code></td>
     * <td align=right><code>400</code></td>
     * </tr>
     * <tr>
     * <td>❌ <code>FAILURE</code></td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td align=right><code>409</code></td>
     * </tr>
     * <tr>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>❌ <code>FAILURE</code></td>
     * <td align=right><code>409</code></td>
     * </tr>
     * <tr>
     * <td rowspan="4">Exceeds Limits</td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td align=right>N/A</td>
     * </tr>
     * <tr>
     * <td>❌ <code>FAILURE</code></td>
     * <td>❌ <code>FAILURE</code></td>
     * <td align=right>N/A</td>
     * </tr>
     * <tr>
     * <td>❌ <code>FAILURE</code></td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td align=right>N/A</td>
     * </tr>
     * <tr>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>❌ <code>FAILURE</code></td>
     * <td align=right>N/A</td>
     * </tr>
     * <tr>
     * <td rowspan="8"><CODE>CANCEL_ONLY</CODE></td>
     * <td rowspan="4">Within Limits</td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td align=right><code>200</code></td>
     * </tr>
     * <tr>
     * <td>❌ <code>FAILURE</code></td>
     * <td>❌ <code>FAILURE</code></td>
     * <td align=right><code>400</code></td>
     * </tr>
     * <tr>
     * <td>❌ <code>FAILURE</code></td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td align=right><code>409</code></td>
     * </tr>
     * <tr>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>❌ <code>FAILURE</code></td>
     * <td align=right><code>409</code></td>
     * </tr>
     * <tr>
     * <td rowspan="4">Exceeds Limits</td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td align=right><code>200</code></td>
     * </tr>
     * <tr>
     * <td>❌ <code>FAILURE</code></td>
     * <td>❌ <code>FAILURE</code></td>
     * <td align=right><code>400</code></td>
     * </tr>
     * <tr>
     * <td>❌ <code>FAILURE</code></td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td align=right>N/A</td>
     * </tr>
     * <tr>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>❌ <code>FAILURE</code></td>
     * <td align=right><code>409</code></td>
     * </tr>
     * </tbody>
     * </table>
     *
     * Notes:
     *
     * If both `cancelOrderId` and `cancelOrigClientOrderId` parameters are provided, the `cancelOrderId` is searched first, then the `cancelOrigClientOrderId` from that result is checked against that order. If both conditions are not met the request will be rejected.
     *
     * `cancelNewClientOrderId` will replace `clientOrderId` of the canceled order, freeing it up for new orders.
     *
     * `newClientOrderId` specifies `clientOrderId` value for the placed order.
     *
     * A new order with the same `clientOrderId` is accepted only when the previous one is filled or expired.
     *
     * The new order can reuse old `clientOrderId` of the canceled order.
     *
     * This cancel-replace operation is **not transactional**.
     *
     * If one operation succeeds but the other one fails, the successful operation is still executed.
     *
     * For example, in `STOP_ON_FAILURE` mode, if the new order placement fails, the old order is still canceled.
     *
     * Filters and order count limits are evaluated before cancellation and order placement occurs.
     *
     * If new order placement is not attempted, your order count is still incremented.
     *
     * Like [`order.cancel`](#order-cancel), if you cancel an individual order from an order list, the entire order list is canceled.
     *
     * The performance for canceling an order (single cancel or as part of a cancel-replace) is always better when only `orderId` is sent. Sending `origClientOrderId` or both `orderId` + `origClientOrderId` will be slower.
     *
     * @summary Cancel and replace order (TRADE)
     * @param {OrderCancelReplaceRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<OrderCancelReplaceResponse>>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/trade#order-cancel-replace Binance API Documentation}
     */
    orderCancelReplace(
        requestParameters: OrderCancelReplaceRequest
    ): Promise<WebsocketApiResponse<OrderCancelReplaceResponse>> {
        return this.tradeApi.orderCancelReplace(requestParameters);
    }

    /**
     * Cancel an active order list.
     *
     * Weight(IP): 1
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     * Notes:
     *
     * If both `orderListId` and `listClientOrderId` parameters are provided, the `orderListId` is searched first, then the `listClientOrderId` from that result is checked against that order. If both conditions are not met the request will be rejected.
     *
     * Canceling an individual order with [`order.cancel`](#order-cancel) will cancel the entire order list as well.
     *
     * @summary Cancel Order list (TRADE)
     * @param {OrderListCancelRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<OrderListCancelResponse>>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/trade#order-list-cancel Binance API Documentation}
     */
    orderListCancel(
        requestParameters: OrderListCancelRequest
    ): Promise<WebsocketApiResponse<OrderListCancelResponse>> {
        return this.tradeApi.orderListCancel(requestParameters);
    }

    /**
     * Send in a new one-cancels-the-other (OCO) pair:
     * `LIMIT_MAKER` + `STOP_LOSS`/`STOP_LOSS_LIMIT` orders (called *legs*),
     * where activation of one order immediately cancels the other.
     *
     * This adds 1 order to `EXCHANGE_MAX_ORDERS` filter and the `MAX_NUM_ORDERS` filter
     *
     * Weight(IP): 1
     *
     * Unfilled Order Count: 1
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     * Notes:
     *
     * `listClientOrderId` parameter specifies `listClientOrderId` for the OCO pair.
     *
     * A new OCO with the same `listClientOrderId` is accepted only when the previous one is filled or completely expired.
     *
     * `listClientOrderId` is distinct from `clientOrderId` of individual orders.
     *
     * `limitClientOrderId` and `stopClientOrderId` specify `clientOrderId` values for both legs of the OCO.
     *
     * A new order with the same `clientOrderId` is accepted only when the previous one is filled or expired.
     *
     * Price restrictions on the legs:
     *
     * | `side` | Price relation |
     * | ------ | -------------- |
     * | `BUY`  | `price` < market price < `stopPrice` |
     * | `SELL` | `price` > market price > `stopPrice` |
     *
     * Both legs have the same `quantity`.
     *
     * However, you can set different iceberg quantity for individual legs.
     *
     * If `stopIcebergQty` is used, `stopLimitTimeInForce` must be `GTC`.
     *
     * `trailingDelta` applies only to the `STOP_LOSS`/`STOP_LOSS_LIMIT` leg of the OCO.
     *
     * @summary Place new OCO - Deprecated (TRADE)
     * @param {OrderListPlaceRequest} requestParameters Request parameters.
     * @deprecated
     * @returns Promise<WebsocketApiResponse<OrderListPlaceResponse>>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/trade#order-list-place Binance API Documentation}
     */
    orderListPlace(
        requestParameters: OrderListPlaceRequest
    ): Promise<WebsocketApiResponse<OrderListPlaceResponse>> {
        return this.tradeApi.orderListPlace(requestParameters);
    }

    /**
     * Send in an one-cancels-the-other (OCO) pair, where activation of one order immediately cancels the other.
     *
     * An OCO has 2 orders called the **above order** and **below order**.
     *
     * One of the orders must be a `LIMIT_MAKER/TAKE_PROFIT/TAKE_PROFIT_LIMIT` order and the other must be
     * `STOP_LOSS` or `STOP_LOSS_LIMIT` order.
     *
     * Price restrictions:
     * If the OCO is on the `SELL` side:
     * `LIMIT_MAKER/TAKE_PROFIT_LIMIT` `price` > Last Traded Price > `STOP_LOSS/STOP_LOSS_LIMIT`
     * `stopPrice`
     * `TAKE_PROFIT stopPrice` > Last Traded Price > `STOP_LOSS/STOP_LOSS_LIMIT
     * stopPrice`
     * If the OCO is on the `BUY` side:
     * `LIMIT_MAKER` `price` < Last Traded Price < `STOP_LOSS/STOP_LOSS_LIMIT` `stopPrice`
     * `TAKE_PROFIT stopPrice` > Last Traded Price > `STOP_LOSS/STOP_LOSS_LIMIT stopPrice`
     *  OCOs add **2 orders** to the `EXCHANGE_MAX_ORDERS` filter and `MAX_NUM_ORDERS` filter.
     *
     * Weight(IP): 1
     *
     * Unfilled Order Count: 2
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     * @summary Place new Order list - OCO (TRADE)
     * @param {OrderListPlaceOcoRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<OrderListPlaceOcoResponse>>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/trade#order-list-place-oco Binance API Documentation}
     */
    orderListPlaceOco(
        requestParameters: OrderListPlaceOcoRequest
    ): Promise<WebsocketApiResponse<OrderListPlaceOcoResponse>> {
        return this.tradeApi.orderListPlaceOco(requestParameters);
    }

    /**
     * Place an [OPO](/products/spot/faqs/opo).
     *
     * OPOs add 2 orders to the EXCHANGE_MAX_NUM_ORDERS filter and MAX_NUM_ORDERS filter.
     *
     * Weight(IP): 1
     *
     * Unfilled Order Count: 2
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     * @summary OPO (TRADE)
     * @param {OrderListPlaceOpoRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<OrderListPlaceOpoResponse>>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/trade#order-list-place-opo Binance API Documentation}
     */
    orderListPlaceOpo(
        requestParameters: OrderListPlaceOpoRequest
    ): Promise<WebsocketApiResponse<OrderListPlaceOpoResponse>> {
        return this.tradeApi.orderListPlaceOpo(requestParameters);
    }

    /**
     * Place an [OPOCO](/products/spot/faqs/opo).
     *
     * Weight(IP): 1
     *
     * Unfilled Order Count: 3
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     * @summary OPOCO (TRADE)
     * @param {OrderListPlaceOpocoRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<OrderListPlaceOpocoResponse>>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/trade#order-list-place-opoco Binance API Documentation}
     */
    orderListPlaceOpoco(
        requestParameters: OrderListPlaceOpocoRequest
    ): Promise<WebsocketApiResponse<OrderListPlaceOpocoResponse>> {
        return this.tradeApi.orderListPlaceOpoco(requestParameters);
    }

    /**
     * Places an OTO.
     *
     * An OTO (One-Triggers-the-Other) is an order list comprised of 2 orders.
     *
     * The first order is called the **working order** and must be `LIMIT` or `LIMIT_MAKER`. Initially, only the
     * working order goes on the order book.
     *
     * The second order is called the **pending order**. It can be any order type except for `MARKET` orders using
     * parameter `quoteOrderQty`. The pending order is only placed on the order book when the working order gets
     **fully filled**.
     *
     * If either the working order or the pending order is cancelled individually, the other order in the order list
     * will also be canceled or expired.
     *
     * When the order list is placed, if the working order gets **immediately fully filled**, the placement response
     * will show the working order as `FILLED` but the pending order will still appear as `PENDING_NEW`. You need to
     * query the status of the pending order again to see its updated status.
     *
     * OTOs add **2 orders** to the `EXCHANGE_MAX_NUM_ORDERS` filter and `MAX_NUM_ORDERS` filter.
     *
     * Weight(IP): 1
     *
     * Unfilled Order Count: 2
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     **Mandatory parameters based on `pendingType` or `workingType`**
     *
     * Depending on the `pendingType` or `workingType`, some optional parameters will become mandatory.
     *
     * |Type                                                  |Additional mandatory parameters|Additional information|
     * |----                                                  |----                           |------
     * |`workingType` = `LIMIT`                               |`workingTimeInForce`           |
     * |`pendingType` = `LIMIT`                                |`pendingPrice`, `pendingTimeInForce`          |
     * |`pendingType` = `STOP_LOSS` or `TAKE_PROFIT`           |`pendingStopPrice` and/or `pendingTrailingDelta`|
     * |`pendingType` =`STOP_LOSS_LIMIT` or `TAKE_PROFIT_LIMIT`|`pendingPrice`, `pendingStopPrice` and/or `pendingTrailingDelta`, `pendingTimeInForce`|
     *
     * @summary Place new Order list - OTO (TRADE)
     * @param {OrderListPlaceOtoRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<OrderListPlaceOtoResponse>>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/trade#order-list-place-oto Binance API Documentation}
     */
    orderListPlaceOto(
        requestParameters: OrderListPlaceOtoRequest
    ): Promise<WebsocketApiResponse<OrderListPlaceOtoResponse>> {
        return this.tradeApi.orderListPlaceOto(requestParameters);
    }

    /**
     * Place an OTOCO.
     *
     * An OTOCO (One-Triggers-One-Cancels-the-Other) is an order list comprised of 3 orders.
     * The first order is called the **working order** and must be `LIMIT` or `LIMIT_MAKER`. Initially, only the working order goes on the order book.
     * The behavior of the working order is the same as the [OTO](#order-list-place-oto).
     * OTOCO has 2 pending orders (pending above and pending below), forming an OCO pair. The pending orders are only placed on the order book when the working order gets **fully filled**.
     * The rules of the pending above and pending below follow the same rules as the [Order list OCO](#order-list-place-oco).
     * OTOCOs add **3 orders** to the `EXCHANGE_MAX_NUM_ORDERS` filter and `MAX_NUM_ORDERS` filter.
     *
     * Weight(IP): 1
     *
     * Unfilled Order Count: 3
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     **Mandatory parameters based on `pendingAboveType`, `pendingBelowType` or `workingType`**
     *
     * Depending on the `pendingAboveType`/`pendingBelowType` or `workingType`, some optional parameters will become mandatory.
     *
     * |Type                                                       |Additional mandatory parameters|Additional information|
     * |----                                                       |----                           |------
     * |`workingType` = `LIMIT`                                    |`workingTimeInForce`           |
     * |`pendingAboveType`= `LIMIT_MAKER`                                |`pendingAbovePrice`          |
     * |`pendingAboveType` = `STOP_LOSS/TAKE_PROFIT`         |`pendingAboveStopPrice` and/or `pendingAboveTrailingDelta`|
     * |`pendingAboveType=STOP_LOSS_LIMIT/TAKE_PROFIT_LIMIT`|`pendingAbovePrice`, `pendingAboveStopPrice` and/or `pendingAboveTrailingDelta`, `pendingAboveTimeInForce`|
     * |`pendingBelowType`= `LIMIT_MAKER`                                |`pendingBelowPrice`          |
     * `pendingBelowType= STOP_LOSS/TAKE_PROFIT`         |`pendingBelowStopPrice` and/or `pendingBelowTrailingDelta`|
     * |`pendingBelowType=STOP_LOSS_LIMIT/TAKE_PROFIT_LIMIT`|`pendingBelowPrice`, `pendingBelowStopPrice` and/or `pendingBelowTrailingDelta`, `pendingBelowTimeInForce`|
     *
     * @summary Place new Order list - OTOCO (TRADE)
     * @param {OrderListPlaceOtocoRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<OrderListPlaceOtocoResponse>>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/trade#order-list-place-otoco Binance API Documentation}
     */
    orderListPlaceOtoco(
        requestParameters: OrderListPlaceOtocoRequest
    ): Promise<WebsocketApiResponse<OrderListPlaceOtocoResponse>> {
        return this.tradeApi.orderListPlaceOtoco(requestParameters);
    }

    /**
     * Send in a new order.
     *
     * This adds 1 order to the `EXCHANGE_MAX_ORDERS` filter and the `MAX_NUM_ORDERS` filter.
     *
     * Weight(IP): 1
     *
     * Unfilled Order Count: 1
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     * <a id="order-type">Certain parameters (*)</a> become mandatory based on the order `type`:
     *
     * <table>
     * <thead>
     * <tr>
     * <th>Order <code>type</code></th>
     * <th>Mandatory parameters</th>
     * </tr>
     * </thead>
     * <tbody>
     * <tr>
     * <td><code>LIMIT</code></td>
     * <td>
     * <ul>
     * <li><code>timeInForce</code></li>
     * <li><code>price</code></li>
     * <li><code>quantity</code></li>
     * </ul>
     * </td>
     * </tr>
     * <tr>
     * <td><code>LIMIT_MAKER</code></td>
     * <td>
     * <ul>
     * <li><code>price</code></li>
     * <li><code>quantity</code></li>
     * </ul>
     * </td>
     * </tr>
     * <tr>
     * <td><code>MARKET</code></td>
     * <td>
     * <ul>
     * <li><code>quantity</code> or <code>quoteOrderQty</code></li>
     * </ul>
     * </td>
     * </tr>
     * <tr>
     * <td><code>STOP_LOSS</code></td>
     * <td>
     * <ul>
     * <li><code>quantity</code></li>
     * <li><code>stopPrice</code> or <code>trailingDelta</code></li>
     * </ul>
     * </td>
     * </tr>
     * <tr>
     * <td><code>STOP_LOSS_LIMIT</code></td>
     * <td>
     * <ul>
     * <li><code>timeInForce</code></li>
     * <li><code>price</code></li>
     * <li><code>quantity</code></li>
     * <li><code>stopPrice</code> or <code>trailingDelta</code></li>
     * </ul>
     * </td>
     * </tr>
     * <tr>
     * <td><code>TAKE_PROFIT</code></td>
     * <td>
     * <ul>
     * <li><code>quantity</code></li>
     * <li><code>stopPrice</code> or <code>trailingDelta</code></li>
     * </ul>
     * </td>
     * </tr>
     * <tr>
     * <td><code>TAKE_PROFIT_LIMIT</code></td>
     * <td>
     * <ul>
     * <li><code>timeInForce</code></li>
     * <li><code>price</code></li>
     * <li><code>quantity</code></li>
     * <li><code>stopPrice</code> or <code>trailingDelta</code></li>
     * </ul>
     * </td>
     * </tr>
     * </tbody>
     * </table>
     *
     * Supported order types:
     *
     * <table>
     * <thead>
     * <tr>
     * <th>Order <code>type</code></th>
     * <th>Description</th>
     * </tr>
     * </thead>
     * <tbody>
     * <tr>
     * <td><code>LIMIT</code></td>
     * <td>
     * <p>
     * Buy or sell <code>quantity</code> at the specified <code>price</code> or better.
     * </p>
     * </td>
     * </tr>
     * <tr>
     * <td><code>LIMIT_MAKER</code></td>
     * <td>
     * <p>
     * <code>LIMIT</code> order that will be rejected if it immediately matches and trades as a taker.
     * </p>
     * <p>
     * This order type is also known as a POST-ONLY order.
     * </p>
     * </td>
     * </tr>
     * <tr>
     * <td><code>MARKET</code></td>
     * <td>
     * <p>
     * Buy or sell at the best available market price.
     * </p>
     * <ul>
     * <li>
     * <p>
     * <code>MARKET</code> order with <code>quantity</code> parameter
     * specifies the amount of the <em>base asset</em> you want to buy or sell.
     * Actually executed quantity of the quote asset will be determined by available market liquidity.
     * </p>
     * <p>
     * E.g., a MARKET BUY order on BTCUSDT for <code>"quantity": "0.1000"</code>
     * specifies that you want to buy 0.1 BTC at the best available price.
     * If there is not enough BTC at the best price, keep buying at the next best price,
     * until either your order is filled, or you run out of USDT, or market runs out of BTC.
     * </p>
     * </li>
     * <li>
     * <p>
     * <code>MARKET</code> order with <code>quoteOrderQty</code> parameter
     * specifies the amount of the <em>quote asset</em> you want to spend (when buying) or receive (when selling).
     * Actually executed quantity of the base asset will be determined by available market liquidity.
     * </p>
     * <p>
     * E.g., a MARKET BUY on BTCUSDT for <code>"quoteOrderQty": "100.00"</code>
     * specifies that you want to buy as much BTC as you can for 100 USDT at the best available price.
     * Similarly, a SELL order will sell as much available BTC as needed for you to receive 100 USDT
     * (before commission).
     * </p>
     * </li>
     * </ul>
     * </td>
     * </tr>
     * <tr>
     * <td><code>STOP_LOSS</code></td>
     * <td>
     * <p>
     * Execute a <code>MARKET</code> order for given <code>quantity</code> when specified conditions are met.
     * </p>
     * <p>
     * I.e., when <code>stopPrice</code> is reached, or when <code>trailingDelta</code> is activated.
     * </p>
     * </td>
     * </tr>
     * <tr>
     * <td><code>STOP_LOSS_LIMIT</code></td>
     * <td>
     * <p>
     * Place a <code>LIMIT</code> order with given parameters when specified conditions are met.
     * </p>
     * </td>
     * </tr>
     * <tr>
     * <td><code>TAKE_PROFIT</code></td>
     * <td>
     * <p>
     * Like <code>STOP_LOSS</code> but activates when market price moves in the favorable direction.
     * </p>
     * </td>
     * </tr>
     * <tr>
     * <td><code>TAKE_PROFIT_LIMIT</code></td>
     * <td>
     * <p>
     * Like <code>STOP_LOSS_LIMIT</code> but activates when market price moves in the favorable direction.
     * </p>
     * </td>
     * </tr>
     * </tbody>
     * </table>
     *
     * <a id="pegged-orders-info"></a>
     * Notes on using parameters for Pegged Orders:
     *
     * These parameters are allowed for `LIMIT`, `LIMIT_MAKER`, `STOP_LOSS_LIMIT`, `TAKE_PROFIT_LIMIT` orders.
     * If `pegPriceType` is specified, `price` becomes optional. Otherwise, it is still mandatory.
     * `pegPriceType=PRIMARY_PEG` means the primary peg, that is the best price on the same side of the order book as your order.
     * `pegPriceType=MARKET_PEG` means the market peg, that is the best price on the opposite side of the order book from your order.
     * Use `pegOffsetType` and `pegOffsetValue` to request a price level other than the best one. These parameters must be specified together.
     *
     * <a id="timeInForce"></a>
     *
     * Available `timeInForce` options,
     * setting how long the order should be active before expiration:
     *
     * TIF  | Description
     * ----- | --------------
     * `GTC` | **Good 'til Canceled** – the order will remain on the book until you cancel it, or the order is completely filled.
     * `IOC` | **Immediate or Cancel** – the order will be filled for as much as possible, the unfilled quantity immediately expires.
     * `FOK` | **Fill or Kill** – the order will expire unless it cannot be immediately filled for the entire quantity.
     *
     * Notes:
     *
     * `newClientOrderId` specifies `clientOrderId` value for the order.
     *
     * A new order with the same `clientOrderId` is accepted only when the previous one is filled or expired.
     *
     * Any `LIMIT` or `LIMIT_MAKER` order can be made into an iceberg order by specifying the `icebergQty`.
     *
     * An order with an `icebergQty` must have `timeInForce` set to `GTC`.
     *
     * Trigger order price rules for `STOP_LOSS`/`TAKE_PROFIT` orders:
     *
     * `stopPrice` must be above market price: `STOP_LOSS BUY`, `TAKE_PROFIT SELL`
     * `stopPrice` must be below market price: `STOP_LOSS SELL`, `TAKE_PROFIT BUY`
     *
     * `MARKET` orders using `quoteOrderQty` follow [`LOT_SIZE`](/products/spot/filters#lot_size) filter rules.
     *
     * The order will execute a quantity that has notional value as close as possible to requested `quoteOrderQty`.
     *
     * @summary Place new order (TRADE)
     * @param {OrderPlaceRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<OrderPlaceResponse>>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/trade#order-place Binance API Documentation}
     */
    orderPlace(
        requestParameters: OrderPlaceRequest
    ): Promise<WebsocketApiResponse<OrderPlaceResponse>> {
        return this.tradeApi.orderPlace(requestParameters);
    }

    /**
     * Test order placement.
     *
     * Validates new order parameters and verifies your signature
     * but does not send the order into the matching engine.
     *
     * Weight: | Condition | Request Weight |
     * | --- | --- |
     * | Without `computeCommissionRates` | 1 |
     * | With `computeCommissionRates` | 20 |
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Memory
     *
     * @summary Test new order (TRADE)
     * @param {OrderTestRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<OrderTestResponse>>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/trade#order-test Binance API Documentation}
     */
    orderTest(
        requestParameters: OrderTestRequest
    ): Promise<WebsocketApiResponse<OrderTestResponse>> {
        return this.tradeApi.orderTest(requestParameters);
    }

    /**
     * Places an order using smart order routing (SOR).
     *
     * This adds 1 order to the `EXCHANGE_MAX_ORDERS` filter and the `MAX_NUM_ORDERS` filter.
     *
     * Read [SOR FAQ](/products/spot/faqs/sor_faq) to learn more.
     *
     * Weight(IP): 1
     *
     * Unfilled Order Count: 1
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     **Note:** `sor.order.place` only supports `LIMIT` and `MARKET` orders. `quoteOrderQty` is not supported.
     *
     * @summary Place new order using SOR (TRADE)
     * @param {SorOrderPlaceRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<SorOrderPlaceResponse>>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/trade#sor-order-place Binance API Documentation}
     */
    sorOrderPlace(
        requestParameters: SorOrderPlaceRequest
    ): Promise<WebsocketApiResponse<SorOrderPlaceResponse>> {
        return this.tradeApi.sorOrderPlace(requestParameters);
    }

    /**
     * Test new order creation and signature/recvWindow using smart order routing (SOR).
     * Creates and validates a new order but does not send it into the matching engine.
     *
     * Weight: | Condition | Request Weight |
     * | --- | --- |
     * | Without `computeCommissionRates` | 1 |
     * | With `computeCommissionRates` | 20 |
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Memory
     *
     * @summary Test new order using SOR (TRADE)
     * @param {SorOrderTestRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<SorOrderTestResponse>>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/trade#sor-order-test Binance API Documentation}
     */
    sorOrderTest(
        requestParameters: SorOrderTestRequest
    ): Promise<WebsocketApiResponse<SorOrderTestResponse>> {
        return this.tradeApi.sorOrderTest(requestParameters);
    }

    /**
     * **Note:**
     *
     * Users should track the corresponding subscription status of related accounts as needed.
     *
     * Weight(IP): 2
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Memory
     *
     * @summary Listing all subscriptions
     * @param {SessionSubscriptionsRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<SessionSubscriptionsResponse>>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/user-data-stream#session-subscriptions Binance API Documentation}
     */
    sessionSubscriptions(
        requestParameters: SessionSubscriptionsRequest = {}
    ): Promise<WebsocketApiResponse<SessionSubscriptionsResponse>> {
        return this.userDataStreamApi.sessionSubscriptions(requestParameters);
    }

    /**
     * Subscribe to the User Data Stream in the current WebSocket connection.
     *
     **Notes:**
     * - This method requires an authenticated WebSocket connection using Ed25519 keys. Please refer to [`session.logon`](/catalog/core-trading-spot-trading/api/ws-api/auth#session-logon).
     * - To check the subscription status, use [`session.status`](/catalog/core-trading-spot-trading/api/ws-api/auth#session-status), see the `userDataStream` flag indicating you have have an active subscription.
     * - User Data Stream events are available in both JSON and [SBE](/products/spot/faqs/sbe_faq) sessions.
     * - Please refer to [User Data Streams](/products/spot/user-data-stream) for the event format details.
     * - For SBE, only SBE schema 2:1 or later is supported.
     *
     * Weight(IP): 2
     *
     * Security Type: NONE
     *
     * @summary Subscribe to User Data Stream
     * @param {UserDataStreamSubscribeRequest} requestParameters Request parameters.
     * @param {string} [id] Optional custom stream identifier.
     *
     * @returns Promise<{response: WebsocketApiResponse<UserDataStreamSubscribeResponse>, stream: WebsocketStream<object>}>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/user-data-stream#user-data-stream-subscribe Binance API Documentation}
     */
    userDataStreamSubscribe(
        requestParameters: UserDataStreamSubscribeRequest = {},
        id?: string
    ): Promise<{
        response: WebsocketApiResponse<UserDataStreamSubscribeResponse>;
        stream: WebsocketStream<UserDataStreamEventsResponse>;
    }> {
        return this.userDataStreamApi
            .userDataStreamSubscribe(requestParameters)
            .then((response) => {
                const identifier = id || randomString();
                const stream = createStreamHandler<UserDataStreamEventsResponse>(
                    this.websocketBase,
                    identifier
                );
                return { response, stream };
            })
            .catch((error) => {
                throw error;
            });
    }

    /**
     * Weight(IP): 2
     *
     * Security Type: USER_STREAM
     *
     * Notes:
     **Data Source:** Memory
     *
     * @summary Subscribe to User Data Stream through signature subscription (USER_STREAM)
     * @param {UserDataStreamSubscribeSignatureRequest} requestParameters Request parameters.
     * @param {string} [id] Optional custom stream identifier.
     *
     * @returns Promise<{response: WebsocketApiResponse<UserDataStreamSubscribeSignatureResponse>, stream: WebsocketStream<object>}>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/user-data-stream#user-data-stream-subscribe-signature Binance API Documentation}
     */
    userDataStreamSubscribeSignature(
        requestParameters: UserDataStreamSubscribeSignatureRequest = {},
        id?: string
    ): Promise<{
        response: WebsocketApiResponse<UserDataStreamSubscribeSignatureResponse>;
        stream: WebsocketStream<UserDataStreamEventsResponse>;
    }> {
        return this.userDataStreamApi
            .userDataStreamSubscribeSignature(requestParameters)
            .then((response) => {
                const identifier = id || randomString();
                const stream = createStreamHandler<UserDataStreamEventsResponse>(
                    this.websocketBase,
                    identifier
                );
                return { response, stream };
            })
            .catch((error) => {
                throw error;
            });
    }

    /**
     * Stop listening to the User Data Stream in the current WebSocket
     * connection.
     *
     * Note that `session.logout` will only close the subscription created with `userDataStream.subscribe` but not subscriptions opened with `userDataStream.subscribe.signature`.
     *
     * Weight(IP): 2
     *
     * @summary WebSocket Unsubscribe from User Data Stream
     * @param {UserDataStreamUnsubscribeRequest} requestParameters Request parameters.
     *
     * @returns Promise<WebsocketApiResponse<UserDataStreamUnsubscribeResponse>>
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/user-data-stream#user-data-stream-unsubscribe Binance API Documentation}
     */
    userDataStreamUnsubscribe(
        requestParameters: UserDataStreamUnsubscribeRequest = {}
    ): Promise<WebsocketApiResponse<UserDataStreamUnsubscribeResponse>> {
        return this.userDataStreamApi.userDataStreamUnsubscribe(requestParameters);
    }
}
